import { HF_ROUTES } from '@/config/routes';

/**
 * Lookup `attributes.features` keys that enable each nav/route key.
 *
 * Backend sends `transactions` for store/TLog Transactions and
 * `ev transactions` (with a space) for EV Transactions.
 */
export const LOOKUP_FEATURES_BY_NAV_KEY: Partial<Record<keyof typeof HF_ROUTES, string[]>> = {
  dashboard: ['dashboard'],
  stations: ['stations'],
  visits: ['visits'],
  visitsList: ['visits'],
  // Backend `transactions` enables TLog/store Transactions and Product Catalog together.
  storeTransactions: ['transactions', 'storeTransactions'],
  // Transactions section views (Fuel + Store = TLog list, list = EV Transactions): the section's
  // features. The EV list also calls the EV API; the EV feature is not checked here.
  storeTransactionsFuelStore: ['transactions', 'storeTransactions'],
  storeTransactionsList: ['transactions', 'storeTransactions'],
  productCatalog: ['transactions', 'storeTransactions'],
  sitePerformance: ['sitePerformance'],
  transactions: ['ev transactions', 'evTransactions'],
  payments: ['payments'],
  campaigns: ['campaigns'],
  customers: ['customers'],
  people: ['people'],
  settings: ['settings'],
  notifications: ['notifications'],
  support: ['support'],
};

export const normalizeLookupFeature = (key: string) => key.replace(/[\s_-]+/g, '').toLowerCase();

export const hasLookupFeature = (features: string[], lookupKeys: string[]) => {
  const normalizedFeatures = new Set(features.map(normalizeLookupFeature));
  return lookupKeys.some((key) => normalizedFeatures.has(normalizeLookupFeature(key)));
};

export const navLinkEnabledByLookup = (features: string[], navKey: keyof typeof HF_ROUTES) => {
  const lookupKeys = LOOKUP_FEATURES_BY_NAV_KEY[navKey] ?? [navKey];
  return hasLookupFeature(features, lookupKeys);
};

/**
 * Whether the partner may open an EV Transactions list (GET `/accounts/customer/transactions`).
 *
 * - Empty `features` means the lookup is still loading: allowed (same fallback as the nav).
 * - Otherwise: the EV feature (`ev transactions` / `evTransactions`, the `transactions` nav key).
 *   The store `transactions` feature does NOT count: it opens only the TLog views.
 * Used by Transactions > EV Transactions (radio option and route guard) and the Dashboard
 * EV Sales link, so they never disagree.
 */
export const canOpenEvTransactionsList = (features: string[]) =>
  features.length === 0 || navLinkEnabledByLookup(features, 'transactions');

/** Map a lookup feature string to the HF_ROUTES key it should open. */
export const routeKeyForLookupFeature = (
  lookupFeature: string,
): keyof typeof HF_ROUTES | undefined => {
  const normalized = normalizeLookupFeature(lookupFeature);
  const mapped = Object.entries(LOOKUP_FEATURES_BY_NAV_KEY).find(([, keys]) =>
    keys.some((key) => normalizeLookupFeature(key) === normalized),
  );

  if (mapped) {
    return mapped[0] as keyof typeof HF_ROUTES;
  }

  return lookupFeature as keyof typeof HF_ROUTES;
};

export const matchLookupMapKey = <T extends string>(
  lookupFeature: string,
  map: Record<T, unknown>,
): T | undefined => {
  if (lookupFeature in map) {
    return lookupFeature as T;
  }

  const normalized = normalizeLookupFeature(lookupFeature);
  return (Object.keys(map) as T[]).find((key) => normalizeLookupFeature(key) === normalized);
};

/**
 * Post-login / brand home path:
 * Dashboard when available, otherwise Stations, then Site Performance, then first visible nav link.
 */
export const getDefaultHomePath = (
  features: string[],
  navKeys: Array<keyof typeof HF_ROUTES> = [],
) => {
  if (features.length === 0 || navLinkEnabledByLookup(features, 'dashboard')) {
    return HF_ROUTES.dashboard;
  }

  if (navLinkEnabledByLookup(features, 'stations')) {
    return HF_ROUTES.stations;
  }

  if (navLinkEnabledByLookup(features, 'sitePerformance')) {
    return HF_ROUTES.sitePerformance;
  }

  const firstNavKey = navKeys.find((key) => navLinkEnabledByLookup(features, key));
  if (firstNavKey) {
    return HF_ROUTES[firstNavKey];
  }

  return HF_ROUTES.dashboard;
};

/**
 * Home path for the signed-in partner (root redirect, brand logo, unknown-URL fallback).
 *
 * - `soleStationId` set (partner has exactly one station): always My Site. That partner can
 *   open only My Site and Settings, so the multi-station home pages would bounce.
 * - Otherwise: `getDefaultHomePath`, unchanged.
 */
export const getHomePath = (
  features: string[],
  navKeys: Array<keyof typeof HF_ROUTES> = [],
  soleStationId?: string,
) => (soleStationId ? HF_ROUTES.mySite : getDefaultHomePath(features, navKeys));
